import { HttpClientModule } from '@angular/common/http';
import { ModuleWithProviders, NgModule } from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';
import { NgxsReduxDevtoolsPluginModule } from '@ngxs/devtools-plugin';
import { NgxsStoragePluginModule, SESSION_STORAGE_ENGINE, STORAGE_ENGINE } from '@ngxs/storage-plugin';
import { NgxsModule } from '@ngxs/store';
import {
  AccountConfigurationState,
  AccountServicesModule,
  AuthContextState,
  AuthFormState,
  AuthInterceptor,
  DisabledDetailsState,
  EmailAuthenticationState,
  LinkAccountState,
  OAuthState,
  PasswordUpdateState,
  SessionState,
  UpdatedTermsState
} from '@platform-frontend/account';
import { AccountUiModule } from '@platform-frontend/account-ui';
import {
  AcademicState,
  AssetState,
  CheckoutBillingInformationState,
  CheckoutMatomoInterceptor,
  CheckoutPaymentInformationState,
  DrawDialogModule,
  FlowState,
  MetadataIdInterceptor,
  PaymentWalletState,
  ProductState,
  ShoppingCartBillingState,
  ShoppingCartPaymentState,
  ShoppingCartPersonalizationState,
  ShoppingCartState,
  StartupConfigurationState,
  StartupTempDataState,
  SubscriptionBillingState,
  SubscriptionPaymentState,
  VendorState
} from '@platform-frontend/checkout';
import { CountryState, MarketplaceBillingInformationState, MarketplacePaymentInformationState } from '@platform-frontend/marketplace';
import { ENABLE_DEBUG_FEATURES, EnvironmentLabelModule, LegalModule, PLATFORM_ENVIRONMENT } from '@platform-frontend/platform';
import { ENGLISH, GERMAN, TranslocoConfigModule } from '@platform-frontend/transloco';

import { DeviceFingerprintServiceInitializer } from '@platform-frontend/payment-util';
import {
  ButtonModule,
  IconsModule,
  LoadingIndicatorModule,
  LoadingState,
  LottieConfigModule,
  NotificationModule,
  PopupServiceInitializer,
  icoAcademic,
  icoChevronDown,
  icoChevronUp,
  icoCogDuo,
  icoCross,
  icoDeleteDuo,
  icoHistory,
  icoLoaderLight,
  icoSignIn,
  icoSignOut,
  icoSignOutDuo,
  icoSubscriptionDuo,
  icoTick,
  icoTickCircleDuo,
  icoTranslateDuo,
  icoUserCircleDuo
} from '@platform-frontend/ui-tools';
import { ErrorModule, NotificationState, RouterExtensionsModule, WindowProvider } from '@platform-frontend/util';
import player from 'lottie-web/build/player/lottie_svg';
import { NgxMaskModule } from 'ngx-mask';
import { provideMatomo, withRouter, withRouterInterceptors } from 'ngx-matomo-client';
import { AcademicRegistrationModule } from './academic-registration/academic-registration.module';
import { AccountDisabledService } from './account/account-disabled.service';
import { AuthenticationService } from './account/authentication.service';
import { AppRoutingModule } from './app-routing.module';
import { CommercialCheckoutModule } from './commercial-checkout/commercial-checkout.module';
import { PersonalizationModule } from './commercial-checkout/personalization/personalization.module';
import { CommercialCommonModule } from './commercial-common/commercial-common.module';
import { CustomStorageEngine } from './custom-storage-engine';
import { ENVIRONMENT } from './environment';
import { WebCheckoutIntegrationModule } from './integration/web-checkout-integration.module';
import { WebCheckoutIntegrationServiceInitializer } from './integration/web-checkout-integration.service';
import { WebCheckoutRedirectAnchorInterceptor } from './integration/web-checkout-redirect-anchor-interceptor';
import { LayoutModule } from './layout/layout.module';
import { NavigationServiceInitializer } from './navigation.service';
import { PurchaseInformationModule } from './purchase-information/purchase-information.module';
import { SubscriptionManagementModule } from './subscription-management/subscription-management.module';
import { WebCheckoutComponent } from './web-checkout.component';

let EXTRA_MODULES: ModuleWithProviders<object>[] = [];
if (ENABLE_DEBUG_FEATURES) {
  EXTRA_MODULES = [NgxsReduxDevtoolsPluginModule.forRoot()];
}

@NgModule({
  declarations: [WebCheckoutComponent],
  imports: [
    AccountServicesModule.withServices(AuthenticationService, AccountDisabledService),
    AccountUiModule,
    AcademicRegistrationModule,
    AppRoutingModule,
    BrowserModule,
    ButtonModule,
    EnvironmentLabelModule,
    CommercialCheckoutModule,
    CommercialCommonModule,
    DrawDialogModule,
    ErrorModule.forRoot(),
    HttpClientModule,
    IconsModule.withIcons(
      icoAcademic,
      icoCross,
      icoCogDuo,
      icoDeleteDuo,
      icoHistory,
      icoLoaderLight,
      icoSignIn,
      icoSignOut,
      icoSignOutDuo,
      icoSubscriptionDuo,
      icoTickCircleDuo,
      icoChevronDown,
      icoChevronUp,
      icoUserCircleDuo,
      icoTick,
      icoTranslateDuo
    ),
    LayoutModule,
    LegalModule,
    LoadingIndicatorModule.forRoot(),
    LottieConfigModule.withOptions({ player: () => player }),
    NgxMaskModule.forRoot(),
    NgxsModule.forRoot(
      [
        // account:
        AccountConfigurationState,
        AuthContextState,
        AuthFormState,
        DisabledDetailsState,
        EmailAuthenticationState,
        LinkAccountState,
        OAuthState,
        PasswordUpdateState,
        SessionState,
        UpdatedTermsState,
        // checkout:
        AcademicState,
        AssetState,
        CheckoutBillingInformationState,
        CheckoutPaymentInformationState,
        CountryState,
        FlowState,
        PaymentWalletState,
        ProductState,
        ShoppingCartState,
        ShoppingCartBillingState,
        ShoppingCartPaymentState,
        ShoppingCartPersonalizationState,
        StartupConfigurationState,
        StartupTempDataState,
        SubscriptionBillingState,
        SubscriptionPaymentState,
        VendorState,
        // marketplace:
        MarketplaceBillingInformationState,
        MarketplacePaymentInformationState,
        // util:
        LoadingState,
        NotificationState
      ],
      { developmentMode: PLATFORM_ENVIRONMENT.isDevelopment }
    ),
    NgxsStoragePluginModule.forRoot({
      key: [
        'session.session',
        'disabledDetails',
        'emailAuthentication.verificationRef',
        'emailAuthentication.email',
        'passwordUpdate.verificationRef',
        'passwordUpdate.email',
        { key: 'shoppingCartPersonalization', engine: SESSION_STORAGE_ENGINE }
      ]
    }),
    NotificationModule,
    PersonalizationModule,
    PurchaseInformationModule,
    RouterExtensionsModule.forRoot({ qualifyReplaceUrl: true }),
    SubscriptionManagementModule,
    TranslocoConfigModule.withConfiguration(!ENABLE_DEBUG_FEATURES, PLATFORM_ENVIRONMENT.apiUrl, {
      availableLangs: [ENGLISH, GERMAN],
      defaultLang: ENGLISH.id
    }),
    WebCheckoutIntegrationModule,
    ...EXTRA_MODULES
  ],
  providers: [
    AuthInterceptor.getProvider(),
    DeviceFingerprintServiceInitializer,
    MetadataIdInterceptor.getProvider(),
    NavigationServiceInitializer,
    PopupServiceInitializer,
    provideMatomo(
      {
        trackerUrl: 'https://yatta-analytics.matomo.cloud',
        siteId: ENVIRONMENT.matomoSiteID
      },
      withRouter(),
      withRouterInterceptors([CheckoutMatomoInterceptor])
    ),
    WebCheckoutIntegrationServiceInitializer,
    WebCheckoutRedirectAnchorInterceptor.getProvider(),
    WindowProvider,
    {
      provide: STORAGE_ENGINE,
      useClass: CustomStorageEngine
    }
  ],
  bootstrap: [WebCheckoutComponent]
})
export class AppModule {}
